/**
 * TokenPicker — MUI Popover listing available tokens for a document type.
 * Clicking a token calls onInsert(tokenString) so the caller can inject
 * "{{token.key}}" into a text field.
 */
import React, { useState } from 'react';
import {
  Box, Button, Chip, Divider, InputAdornment, Popover, TextField, Typography
} from '@mui/material';
import DataObjectIcon from '@mui/icons-material/DataObject';

export default function TokenPicker({ catalog = [], onInsert }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [search, setSearch] = useState('');

  const handleOpen = (e) => setAnchorEl(e.currentTarget);
  const handleClose = () => { setAnchorEl(null); setSearch(''); };

  const filtered = search
    ? catalog.map(group => ({
        ...group,
        tokens: group.tokens.filter(t =>
          t.key.toLowerCase().includes(search.toLowerCase()) ||
          t.label.toLowerCase().includes(search.toLowerCase())
        ),
      })).filter(g => g.tokens.length > 0)
    : catalog;

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        startIcon={<DataObjectIcon />}
        onClick={handleOpen}
        sx={{ fontSize: '0.75rem' }}
      >
        Insert Token
      </Button>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        PaperProps={{ sx: { width: 320, maxHeight: 440, overflow: 'hidden', display: 'flex', flexDirection: 'column' } }}
      >
        <Box sx={{ p: 1 }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Search tokens…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start"><DataObjectIcon fontSize="small" /></InputAdornment>,
            }}
          />
        </Box>
        <Divider />
        <Box sx={{ overflow: 'auto', flex: 1 }}>
          {filtered.map((group) => (
            <Box key={group.category}>
              <Typography variant="caption" sx={{ px: 2, py: 0.5, display: 'block', fontWeight: 600, bgcolor: 'action.hover' }}>
                {group.category}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, p: 1 }}>
                {group.tokens.map((t) => (
                  <Chip
                    key={t.key}
                    label={t.label}
                    size="small"
                    variant="outlined"
                    title={`{{${t.key}}}`}
                    onClick={() => { onInsert(`{{${t.key}}}`); handleClose(); }}
                    sx={{ cursor: 'pointer', fontSize: '0.7rem' }}
                  />
                ))}
              </Box>
            </Box>
          ))}
          {filtered.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
              No tokens match.
            </Typography>
          )}
        </Box>
      </Popover>
    </>
  );
}
